import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { io, type Socket } from "socket.io-client";

const HANDOFF_OFFER_RE =
  /(connect\s+you\s+with\s+(a\s+)?team\s+member|hand\s+this\s+over\s+to\s+(a\s+)?human|human\s+agent|talk\s+to\s+(a\s+)?human|hand\s*off)/i;

type ThemeMode = "system" | "light" | "dark";

type WidgetSettings = {
  primaryColor?: string;
  position?: "left" | "right" | string;
  welcomeMessage?: string;
  agentName?: string;
  theme?: ThemeMode;
  radius?: number;

  launcherMode?: "ICON_ONLY" | "TEXT_ONLY" | "ICON_TEXT";
  launcherText?: string | null;
  launcherIconType?: "WEYNI_LOGO" | "CHAT_ICON" | "CUSTOM_UPLOAD";
  launcherCustomImagePath?: string | null;
  launcherTextColor?: string;
};

type StartSessionResponse = {
  workspaceId: string;
  visitorId: string;
  conversationId: string;
  sessionId: string;
  widgetSettings: WidgetSettings | null;
  aiEnabled: boolean;
  support: {
    conversationMode: "AI_ONLY" | "HUMAN_ONLY" | "HYBRID" | string;
    handoffEnabled: boolean;
    alwaysShowTalkToHuman: boolean;
    agentAvailable: boolean;
    offlineCaptureEnabled: boolean;
    offlineCaptureFields: { name?: boolean; email?: boolean; phone?: boolean };
    aiIdentifyAsAi: boolean;
    aiIdentityStrategy: "AI_ASSISTANT" | "BLENDED" | "BRAND_ASSISTANT" | string;
  };
  conversation: {
    id?: string;
    supportState: string;
    currentResponderType: "ai" | "human" | "none" | string;
    escalationReason: string | null;
    handoffOfferedAt: string | null;
    handoffAcceptedAt: string | null;
  };
};

type Message = {
  id: string;
  senderType: "VISITOR" | "AI" | "AGENT" | string;
  content: string;
  createdAt: string;
};

type VisitorMessageResponse = {
  visitorMessage: Message;
  aiMessage: Message | null;
  provider: string;
  conversation?: {
    id?: string;
    supportState?: string;
    currentResponderType?: string;
    escalationReason?: string | null;
    handoffOfferedAt?: string | null;
    handoffAcceptedAt?: string | null;
  };
  support?: {
    agentAvailable?: boolean;
    offlineCaptureEnabled?: boolean;
    offlineCaptureFields?: { name?: boolean; email?: boolean; phone?: boolean };
    handoffEnabled?: boolean;
  };
};

function resolveThemeMode(mode: ThemeMode): "light" | "dark" {
  if (mode === "light" || mode === "dark") return mode;
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function randomId() {
  try {
    const bytes = new Uint8Array(16);
    // `crypto.getRandomValues` can be unavailable in some older browsers.
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    // Fallback: not cryptographically strong, but good enough for a client-side anonymous ID.
    return (
      Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2)
    );
  }
}

function getStoredAnonymousId(publicKey: string) {
  const key = `weyni_anon:${publicKey}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing && existing.length >= 8) return existing;

    const created = `anon_${randomId()}`;
    localStorage.setItem(key, created);
    return created;
  } catch {
    // Some environments (private browsing / strict policies) can throw on localStorage access.
    return `anon_${randomId()}`;
  }
}

export function WidgetApp(props: {
  publicKey: string;
  apiUrl: string;
  theme?: ThemeMode;
  position?: "left" | "right";
  primaryColor?: string;
  radius?: number;
}) {
  const poweredByHref = "https://weyni.ai";

  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [session, setSession] = useState<StartSessionResponse | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [status, setStatus] = useState<
    "loading" | "ready" | "offline" | "blocked"
  >("loading");
  const [startNonce, setStartNonce] = useState(0);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");
  const [launcherImgFailed, setLauncherImgFailed] = useState(false);
  const [offlineName, setOfflineName] = useState("");
  const [offlineEmail, setOfflineEmail] = useState("");
  const [offlinePhone, setOfflinePhone] = useState("");
  const [offlineMessage, setOfflineMessage] = useState("");
  const [offlineSending, setOfflineSending] = useState(false);
  const [handoffDismissedMessageId, setHandoffDismissedMessageId] = useState<
    string | null
  >(null);
  const [handoffSubmitting, setHandoffSubmitting] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const agentTypingTimeoutRef = useRef<number | null>(null);
  const visitorTypingTimeoutRef = useRef<number | null>(null);
  const visitorTypingSentRef = useRef(false);
  const heartbeatIntervalRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isOpenRef = useRef(false);

  const serverOrigin = useMemo(() => {
    try {
      return new URL(props.apiUrl).origin;
    } catch {
      return "http://localhost:4000";
    }
  }, [props.apiUrl]);

  const poweredByLogoSrc = `${serverOrigin}/weyni.svg`;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia?.("(max-width: 768px), (max-height: 520px)");

    const update = () => {
      const widthCandidates = [
        window.visualViewport?.width,
        window.innerWidth,
        window.document?.documentElement?.clientWidth,
        window.screen?.width,
      ].filter(
        (v): v is number =>
          typeof v === "number" && Number.isFinite(v) && v > 0,
      );

      const heightCandidates = [
        window.visualViewport?.height,
        window.innerHeight,
        window.document?.documentElement?.clientHeight,
        window.screen?.height,
      ].filter(
        (v): v is number =>
          typeof v === "number" && Number.isFinite(v) && v > 0,
      );

      const w = widthCandidates.length ? Math.min(...widthCandidates) : 0;
      const h = heightCandidates.length ? Math.min(...heightCandidates) : 0;

      const smallViewport = (w > 0 && w <= 768) || (h > 0 && h <= 520);
      const matches = Boolean(mq?.matches) || smallViewport;
      setIsFullscreen(matches);
    };

    update();

    const vv = window.visualViewport;
    vv?.addEventListener?.("resize", update);
    vv?.addEventListener?.("scroll", update);
    window.addEventListener("resize", update);
    mq?.addEventListener?.("change", update);

    return () => {
      vv?.removeEventListener?.("resize", update);
      vv?.removeEventListener?.("scroll", update);
      window.removeEventListener("resize", update);
      mq?.removeEventListener?.("change", update);
    };
  }, []);

  const displaySettings: Required<
    Pick<
      WidgetSettings,
      | "primaryColor"
      | "position"
      | "welcomeMessage"
      | "agentName"
      | "theme"
      | "radius"
      | "launcherMode"
      | "launcherText"
      | "launcherIconType"
      | "launcherCustomImagePath"
      | "launcherTextColor"
    >
  > = useMemo(() => {
    const defaults: Required<
      Pick<
        WidgetSettings,
        | "primaryColor"
        | "position"
        | "welcomeMessage"
        | "agentName"
        | "theme"
        | "radius"
        | "launcherMode"
        | "launcherText"
        | "launcherIconType"
        | "launcherCustomImagePath"
        | "launcherTextColor"
      >
    > = {
      primaryColor: "#0B66D4",
      position: "right",
      welcomeMessage: "Hi! How can I help?",
      agentName: "Weyni",
      theme: "system",
      radius: 10,

      launcherMode: "ICON_TEXT",
      launcherText: "Chat",
      launcherIconType: "WEYNI_LOGO",
      launcherCustomImagePath: null,
      launcherTextColor: "#FFFFFF",
    };

    const scriptOverrides: WidgetSettings = {
      primaryColor: props.primaryColor,
      position: props.position,
      theme: props.theme,
      radius: props.radius,
    };

    const serverSettings = session?.widgetSettings ?? null;
    const merged: WidgetSettings = {
      ...defaults,
      ...scriptOverrides,
      ...(serverSettings ?? {}),
    };

    const launcherModeRaw = merged.launcherMode;
    const launcherMode =
      launcherModeRaw === "ICON_ONLY" ||
      launcherModeRaw === "TEXT_ONLY" ||
      launcherModeRaw === "ICON_TEXT"
        ? launcherModeRaw
        : defaults.launcherMode;

    const launcherIconTypeRaw = merged.launcherIconType;
    const launcherIconType =
      launcherIconTypeRaw === "WEYNI_LOGO" ||
      launcherIconTypeRaw === "CHAT_ICON" ||
      launcherIconTypeRaw === "CUSTOM_UPLOAD"
        ? launcherIconTypeRaw
        : defaults.launcherIconType;

    const launcherTextRaw =
      typeof merged.launcherText === "string" ? merged.launcherText : "";
    const launcherText =
      launcherTextRaw.trim().slice(0, 20) || defaults.launcherText;

    const launcherTextColorRaw =
      typeof merged.launcherTextColor === "string"
        ? merged.launcherTextColor.trim()
        : "";
    const launcherTextColor = /^#[0-9a-fA-F]{6}$/.test(launcherTextColorRaw)
      ? launcherTextColorRaw.toUpperCase()
      : defaults.launcherTextColor;

    return {
      primaryColor: merged.primaryColor || defaults.primaryColor,
      position: merged.position || defaults.position,
      welcomeMessage: merged.welcomeMessage || defaults.welcomeMessage,
      agentName: merged.agentName || defaults.agentName,
      theme: merged.theme || defaults.theme,
      radius:
        typeof merged.radius === "number" && Number.isFinite(merged.radius)
          ? merged.radius
          : defaults.radius,

      launcherMode,
      launcherText,
      launcherIconType,
      launcherCustomImagePath:
        typeof merged.launcherCustomImagePath === "string" &&
        merged.launcherCustomImagePath.trim().length > 0
          ? merged.launcherCustomImagePath.trim()
          : null,
      launcherTextColor,
    };
  }, [
    props.primaryColor,
    props.position,
    props.radius,
    props.theme,
    session?.widgetSettings,
  ]);

  useEffect(() => {
    setLauncherImgFailed(false);
  }, [
    displaySettings.launcherIconType,
    displaySettings.launcherCustomImagePath,
  ]);

  useEffect(() => {
    const mode = displaySettings.theme;
    setResolvedTheme(resolveThemeMode(mode));

    if (mode !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = () => setResolvedTheme(resolveThemeMode("system"));

    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [displaySettings.theme]);

  const widgetStyle = useMemo(() => {
    // CSS variables aren’t typed in React’s CSSProperties.
    return {
      ["--weyni-primary"]: displaySettings.primaryColor,
      ["--weyni-radius"]: `${Math.max(8, Math.min(28, displaySettings.radius))}px`,
      ["--weyni-launcher-text"]: displaySettings.launcherTextColor,
    } as unknown as CSSProperties;
  }, [
    displaySettings.primaryColor,
    displaySettings.radius,
    displaySettings.launcherTextColor,
  ]);

  function resolveAssetUrl(value: string) {
    const v = value.trim();
    if (v.startsWith("http://") || v.startsWith("https://")) return v;
    if (v.startsWith("/")) return `${serverOrigin}${v}`;
    return `${serverOrigin}/${v}`;
  }

  function LauncherChatIcon() {
    return (
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M21 15a4 4 0 0 1-4 4H7l-4 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      </svg>
    );
  }

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;

    const anon = getStoredAnonymousId(props.publicKey);
    setStatus("loading");

    async function start() {
      const url = typeof location !== "undefined" ? location.href : undefined;
      const title =
        typeof document !== "undefined" ? document.title : undefined;
      const referrerRaw =
        typeof document !== "undefined" ? document.referrer : undefined;
      const referrer =
        referrerRaw && referrerRaw.trim().length > 0 ? referrerRaw : undefined;

      try {
        const resp = await fetch(
          `${props.apiUrl}/public/widget/${props.publicKey}/session`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              anonymousId: anon,
              page: {
                url,
                title,
                referrer,
              },
            }),
          },
        );

        if (cancelled) return;

        if (!resp.ok) {
          // If the API blocks the origin, fail silently (widget stays hidden).
          setStatus("blocked");
          return;
        }

        const data = (await resp.json()) as StartSessionResponse;
        if (cancelled) return;

        setSession(data);
        setStatus("ready");

        const msgResp = await fetch(
          `${props.apiUrl}/public/widget/${props.publicKey}/conversations/${data.conversationId}/messages`,
        );
        if (!cancelled && msgResp.ok) {
          const list = (await msgResp.json()) as Message[];
          if (!cancelled) setMessages(list);
        }

        // WebSocket (realtime updates)
        const s = io(`${serverOrigin}/ws`, {
          transports: ["websocket"],
          query: {
            publicKey: props.publicKey,
            conversationId: data.conversationId,
          },
        });

        socketRef.current = s;
        s.on("connect", () => setSocketConnected(true));
        s.on("disconnect", () => setSocketConnected(false));
        s.on("message:new", (payload: Message) => {
          setMessages((prev) => {
            if (prev.some((m) => m.id === payload.id)) return prev;
            return [...prev, payload];
          });

          if (!isOpenRef.current && payload.senderType !== "VISITOR") {
            setUnreadCount((c) => Math.min(99, c + 1));
          }
        });

        s.on(
          "conversation:update",
          (payload: {
            id?: string;
            supportState?: string;
            currentResponderType?: string;
            escalationReason?: string | null;
            handoffOfferedAt?: string | null;
            handoffAcceptedAt?: string | null;
          }) => {
            setSession((prev) => {
              if (!prev) return prev;
              if (payload?.id && payload.id !== prev.conversationId)
                return prev;
              return {
                ...prev,
                conversation: {
                  ...prev.conversation,
                  ...payload,
                },
              };
            });
          },
        );

        s.on(
          "typing",
          (payload: {
            conversationId?: string;
            sender?: string;
            isTyping?: boolean;
          }) => {
            if (payload?.sender !== "AGENT") return;
            setAgentTyping(Boolean(payload.isTyping));

            if (agentTypingTimeoutRef.current) {
              window.clearTimeout(agentTypingTimeoutRef.current);
            }
            if (payload.isTyping) {
              agentTypingTimeoutRef.current = window.setTimeout(() => {
                setAgentTyping(false);
                agentTypingTimeoutRef.current = null;
              }, 2500);
            }
          },
        );
      } catch {
        if (!cancelled) setStatus("offline");
      }
    }

    void start();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
      if (heartbeatIntervalRef.current) {
        window.clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (agentTypingTimeoutRef.current) {
        window.clearTimeout(agentTypingTimeoutRef.current);
        agentTypingTimeoutRef.current = null;
      }
      if (visitorTypingTimeoutRef.current) {
        window.clearTimeout(visitorTypingTimeoutRef.current);
        visitorTypingTimeoutRef.current = null;
      }
    };
  }, [props.apiUrl, props.publicKey, serverOrigin, startNonce]);

  function getPageInfo() {
    const url = typeof location !== "undefined" ? location.href : undefined;
    const title = typeof document !== "undefined" ? document.title : undefined;
    const referrerRaw =
      typeof document !== "undefined" ? document.referrer : undefined;
    const referrer =
      referrerRaw && referrerRaw.trim().length > 0 ? referrerRaw : undefined;
    return { url, title, referrer };
  }

  async function postHeartbeat(next: {
    widgetOpen?: boolean;
    messagesSent?: number;
  }) {
    if (!session) return;
    try {
      await fetch(
        `${props.apiUrl}/public/widget/${props.publicKey}/sessions/${session.sessionId}/heartbeat`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            widgetOpen: next.widgetOpen,
            messagesSent: next.messagesSent,
            page: getPageInfo(),
          }),
        },
      );
    } catch {
      // ignore
    }
  }

  async function postEvent(type: string, payload?: Record<string, unknown>) {
    if (!session) return;
    try {
      await fetch(
        `${props.apiUrl}/public/widget/${props.publicKey}/sessions/${session.sessionId}/events`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            type,
            payload,
            widgetOpen: isOpen,
            page: getPageInfo(),
          }),
        },
      );
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    if (!session) return;

    void postEvent("widget_loaded");
    void postHeartbeat({ widgetOpen: isOpen });

    if (heartbeatIntervalRef.current) {
      window.clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    heartbeatIntervalRef.current = window.setInterval(() => {
      void postHeartbeat({ widgetOpen: isOpen });
    }, 15000);

    const onBeforeUnload = () => {
      try {
        const url = `${props.apiUrl}/public/widget/${props.publicKey}/sessions/${session.sessionId}/heartbeat`;
        const body = JSON.stringify({ widgetOpen: false, page: getPageInfo() });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            url,
            new Blob([body], { type: "application/json" }),
          );
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (heartbeatIntervalRef.current) {
        window.clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId]);

  useEffect(() => {
    if (!session) return;
    void postHeartbeat({ widgetOpen: isOpen });
    void postEvent(isOpen ? "widget_open" : "widget_close");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setUnreadCount(0);
    // Let the panel mount before focusing.
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  function emitVisitorTyping(isTyping: boolean) {
    const s = socketRef.current;
    if (!s || !session) return;

    if (isTyping) {
      if (visitorTypingSentRef.current) return;
      visitorTypingSentRef.current = true;
      s.emit("typing", { isTyping: true });

      if (visitorTypingTimeoutRef.current) {
        window.clearTimeout(visitorTypingTimeoutRef.current);
      }
      visitorTypingTimeoutRef.current = window.setTimeout(() => {
        visitorTypingSentRef.current = false;
        s.emit("typing", { isTyping: false });
        visitorTypingTimeoutRef.current = null;
      }, 2000);
      return;
    }

    visitorTypingSentRef.current = false;
    s.emit("typing", { isTyping: false });
    if (visitorTypingTimeoutRef.current) {
      window.clearTimeout(visitorTypingTimeoutRef.current);
      visitorTypingTimeoutRef.current = null;
    }
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isOpen]);

  async function send() {
    const value = text.trim();
    if (!value) return;
    if (!session) return;

    // If the conversation is in offline capture mode, the widget should not
    // send standard chat messages.
    if (
      session.conversation?.supportState === "offline_message" &&
      session.support?.offlineCaptureEnabled
    ) {
      return;
    }

    setIsSending(true);
    setText("");

    try {
      const resp = await fetch(
        `${props.apiUrl}/public/widget/${props.publicKey}/conversations/${session.conversationId}/messages`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ text: value, sessionId: session.sessionId }),
        },
      );

      if (!resp.ok) {
        setIsSending(false);
        return;
      }

      const data = (await resp.json()) as VisitorMessageResponse;

      if (data?.conversation || data?.support) {
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            conversation: {
              ...prev.conversation,
              ...(data.conversation ?? {}),
            },
            support: {
              ...prev.support,
              ...(data.support ?? {}),
            },
          };
        });
      }

      setMessages((prev) => {
        const next = [...prev];
        if (!next.some((m) => m.id === data.visitorMessage.id))
          next.push(data.visitorMessage);
        if (data.aiMessage && !next.some((m) => m.id === data.aiMessage!.id))
          next.push(data.aiMessage);
        return next;
      });
    } finally {
      setIsSending(false);
    }
  }

  const showOfflineCapture = Boolean(
    session &&
    status === "ready" &&
    session.support?.offlineCaptureEnabled &&
    session.conversation?.supportState === "offline_message",
  );

  const lastAiLikeMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (!m) continue;
      if (m.senderType === "AI" || m.senderType === "AGENT") return m;
    }
    return null;
  }, [messages]);

  const handoffOfferDetected = useMemo(() => {
    const c = session?.conversation;
    if (!c) return false;
    if (c.currentResponderType === "human") return false;
    if (c.supportState === "offline_message") return false;

    // Prefer explicit server-driven state when available.
    if (Boolean(c.handoffOfferedAt) && !c.handoffAcceptedAt) return true;
    if (c.escalationReason && /handoff|human/i.test(c.escalationReason))
      return true;
    if (c.supportState && /handoff|human/i.test(c.supportState)) return true;

    // Fallback: detect the common handoff-offer phrasing in the last AI message.
    if (lastAiLikeMessage?.senderType === "AI") {
      return HANDOFF_OFFER_RE.test(lastAiLikeMessage.content);
    }
    return false;
  }, [lastAiLikeMessage, session?.conversation]);

  const showTalkToHuman = Boolean(
    session &&
    status === "ready" &&
    session.support?.handoffEnabled !== false &&
    session.conversation?.currentResponderType !== "human" &&
    (session.support?.alwaysShowTalkToHuman ||
      Boolean(session.conversation?.handoffOfferedAt) ||
      (session.support?.conversationMode ?? "HYBRID") !== "AI_ONLY"),
  );

  const showHandoffPrompt = Boolean(
    session &&
    status === "ready" &&
    session.support?.handoffEnabled !== false &&
    handoffOfferDetected &&
    (!lastAiLikeMessage?.id ||
      lastAiLikeMessage.id !== handoffDismissedMessageId),
  );

  async function requestHuman() {
    if (!session) return;
    try {
      const resp = await fetch(
        `${props.apiUrl}/public/widget/${props.publicKey}/conversations/${session.conversationId}/request-human`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sessionId: session.sessionId }),
        },
      );
      if (!resp.ok) return;
      const data = (await resp.json()) as unknown;
      if (!data || typeof data !== "object") return;
      const payload = data as Record<string, unknown>;
      const supportState =
        typeof payload.supportState === "string" ? payload.supportState : null;
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          conversation: {
            ...prev.conversation,
            supportState: supportState ?? "handoff_requested",
            currentResponderType: "none",
            escalationReason: "human_requested",
            handoffAcceptedAt:
              prev.conversation?.handoffAcceptedAt ?? new Date().toISOString(),
          },
        };
      });
    } catch {
      // ignore
    }
  }

  async function acceptHandoff() {
    if (!session) return;
    if (handoffSubmitting) return;
    setHandoffSubmitting(true);
    try {
      await requestHuman();
      setHandoffDismissedMessageId(lastAiLikeMessage?.id ?? null);
    } finally {
      setHandoffSubmitting(false);
    }
  }

  function declineHandoff() {
    setHandoffDismissedMessageId(lastAiLikeMessage?.id ?? null);
  }

  async function submitOffline() {
    if (!session) return;
    if (offlineSending) return;

    const message = offlineMessage.trim();
    if (!message) return;

    setOfflineSending(true);
    try {
      const resp = await fetch(
        `${props.apiUrl}/public/widget/${props.publicKey}/conversations/${session.conversationId}/offline-message`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            sessionId: session.sessionId,
            name: offlineName.trim() || undefined,
            email: offlineEmail.trim() || undefined,
            phone: offlinePhone.trim() || undefined,
            message,
          }),
        },
      );
      if (!resp.ok) return;

      setOfflineMessage("");
      setOfflineName("");
      setOfflineEmail("");
      setOfflinePhone("");
    } finally {
      setOfflineSending(false);
    }
  }

  if (status === "blocked") {
    return null;
  }

  return (
    <div
      id="weyni-widget"
      data-position={displaySettings.position === "left" ? "left" : "right"}
      data-theme={resolvedTheme}
      data-theme-mode={displaySettings.theme}
      style={widgetStyle}
    >
      {isOpen ? (
        <div
          id="weyni-widget-panel"
          className="weyni-panel"
          style={
            isFullscreen
              ? ({
                  position: "fixed",
                  inset: 0,
                  width: "100vw",
                  height: "100dvh",
                } as CSSProperties)
              : undefined
          }
          role="dialog"
          aria-label="Weyni widget"
          aria-modal={isFullscreen ? true : undefined}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
          }}
          data-state={isOpen ? "open" : "closed"}
        >
          <div className="weyni-header">
            <div className="weyni-title">
              <strong>{displaySettings.agentName || "Weyni"}</strong>
              <span>
                {status === "offline"
                  ? "Offline"
                  : socketConnected
                    ? "Online"
                    : "Connecting…"}{" "}
                · {displaySettings.welcomeMessage}
              </span>
              {agentTyping ? (
                <span className="weyni-typing">Typing…</span>
              ) : null}
            </div>
            <button
              className="weyni-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="weyni-messages" ref={scrollRef}>
            {status === "offline" ? (
              <div className="weyni-notice" role="status">
                <div>We couldn’t connect right now. Please try again.</div>
                <button
                  className="weyni-link"
                  onClick={() => setStartNonce((v) => v + 1)}
                >
                  Retry
                </button>
              </div>
            ) : null}
            {messages.length === 0 ? (
              <div className="weyni-bubble ai">
                {displaySettings.welcomeMessage}
              </div>
            ) : null}
            {messages.map((m) => (
              <div
                key={m.id}
                className={[
                  "weyni-bubble",
                  m.senderType === "VISITOR"
                    ? "visitor"
                    : m.senderType === "AGENT"
                      ? "agent"
                      : "ai",
                ].join(" ")}
              >
                {m.content}
              </div>
            ))}
          </div>

          {showTalkToHuman && !showHandoffPrompt ? (
            <div className="weyni-actions" role="group" aria-label="Actions">
              <button
                type="button"
                className="weyni-secondary"
                onClick={() => void requestHuman()}
                disabled={status !== "ready" || !session}
              >
                Talk to a human
              </button>
            </div>
          ) : null}

          {showOfflineCapture ? (
            <div className="weyni-offline" aria-label="Leave a message">
              <div className="weyni-offline-title">Leave a message</div>
              <div className="weyni-offline-sub">
                A team member isn’t available right now.
              </div>

              {session?.support?.offlineCaptureFields?.name !== false ? (
                <input
                  className="weyni-input"
                  value={offlineName}
                  placeholder="Name (optional)"
                  onChange={(e) => setOfflineName(e.target.value)}
                />
              ) : null}

              {session?.support?.offlineCaptureFields?.email !== false ? (
                <input
                  className="weyni-input"
                  value={offlineEmail}
                  placeholder="Email (optional)"
                  inputMode="email"
                  onChange={(e) => setOfflineEmail(e.target.value)}
                />
              ) : null}

              {session?.support?.offlineCaptureFields?.phone === true ? (
                <input
                  className="weyni-input"
                  value={offlinePhone}
                  placeholder="Phone (optional)"
                  inputMode="tel"
                  onChange={(e) => setOfflinePhone(e.target.value)}
                />
              ) : null}

              <textarea
                className="weyni-textarea"
                value={offlineMessage}
                placeholder="How can we help?"
                rows={4}
                onChange={(e) => setOfflineMessage(e.target.value)}
              />

              <button
                type="button"
                className="weyni-send"
                onClick={() => void submitOffline()}
                disabled={offlineSending || offlineMessage.trim().length === 0}
              >
                {offlineSending ? "Sending…" : "Send message"}
              </button>
            </div>
          ) : showHandoffPrompt ? (
            <div className="weyni-handoff" role="group" aria-label="Handoff">
              <div className="weyni-handoff-title">
                Hand this over to a human agent?
              </div>
              <div className="weyni-handoff-sub">
                We can connect you with a team member.
              </div>
              <div className="weyni-handoff-buttons">
                <button
                  type="button"
                  className="weyni-send"
                  onClick={() => void acceptHandoff()}
                  disabled={handoffSubmitting}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className="weyni-secondary"
                  onClick={declineHandoff}
                  disabled={handoffSubmitting}
                >
                  No
                </button>
              </div>
            </div>
          ) : (
            <div className="weyni-composer">
              <input
                className="weyni-input"
                ref={inputRef}
                value={text}
                placeholder="Type a message…"
                onChange={(e) => {
                  const value = e.target.value;
                  setText(value);
                  emitVisitorTyping(value.trim().length > 0);
                }}
                onBlur={() => emitVisitorTyping(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void send();
                }}
              />
              <button
                className="weyni-send"
                onClick={() => void send()}
                disabled={isSending || !session || status !== "ready"}
              >
                Send
              </button>
            </div>
          )}

          <a
            className="weyni-powered"
            href={poweredByHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Powered by</span>
            <img
              className="weyni-powered-logo"
              src={poweredByLogoSrc}
              alt="Weyni"
              loading="lazy"
            />
            <span>Weyni</span>
          </a>
        </div>
      ) : null}

      {isOpen && isFullscreen ? null : (
        <button
          className="weyni-button"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Open chat"
          aria-expanded={isOpen}
          aria-controls="weyni-widget-panel"
          data-mode={displaySettings.launcherMode}
        >
          {displaySettings.launcherMode !== "TEXT_ONLY" ? (
            <span className="weyni-launcher-icon" aria-hidden="true">
              {displaySettings.launcherIconType === "CHAT_ICON" ? (
                <LauncherChatIcon />
              ) : displaySettings.launcherIconType === "CUSTOM_UPLOAD" ? (
                displaySettings.launcherCustomImagePath &&
                !launcherImgFailed ? (
                  <img
                    className="weyni-launcher-icon-img"
                    src={resolveAssetUrl(
                      displaySettings.launcherCustomImagePath,
                    )}
                    alt=""
                    onError={() => setLauncherImgFailed(true)}
                  />
                ) : (
                  <img
                    className="weyni-launcher-icon-img"
                    src={`${serverOrigin}/weyni.svg`}
                    alt=""
                  />
                )
              ) : (
                <img
                  className="weyni-launcher-icon-img"
                  src={`${serverOrigin}/weyni.svg`}
                  alt=""
                />
              )}
            </span>
          ) : null}

          {displaySettings.launcherMode !== "ICON_ONLY" ? (
            <span className="weyni-launcher-text">
              {displaySettings.launcherText}
            </span>
          ) : null}

          {unreadCount > 0 ? (
            <span className="weyni-badge" aria-label={`${unreadCount} unread`}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </button>
      )}
    </div>
  );
}
